import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import uniqBy from "lodash/uniqBy";
import firstBy from "thenby";

import { ClearingFloat, H2, H3, Divider } from "commons";
import { IN_PROGRESS, isSuccess } from "commons/src/helpers/RequestHelpers";

import { Message } from "../../components/Message";
import { ERROR, getRefetchMessageAndAction } from "../../helpers/ErrorHelpers";
import { requestObject } from "../../reducers/requestsActions";
import { setAddresses } from "../../reducers/addressesActions";
import { retryFetch } from "../../components/DataOperations";
import { EnvelopeIcon } from "./EnvelopeIcon";

export function Addresses({ addresses, requestStatus }) {
    const addressSection = (type, addresses) => {
        const sections = addresses.sort(firstBy("addressId")).map(add => addressDetails(add));
        return (
            <Divider
                key={type}
                data-testid="address-section"
                float="left"
                margin="0px 20px 20px 0px"
            >
                <H3>{type}</H3>
                {sections}
            </Divider>
        );
    };

    const addressDetails = address => (
        <div key={address.addressId}>
            <Divider>
                <Divider float="left">{address.addressLine1}</Divider>
                {address.primary && (
                    <Divider float="left" margin="1px 0px 0px 5px">
                        <EnvelopeIcon color="primary" />
                    </Divider>
                )}
            </Divider>
            <ClearingFloat />
            {address.addressLine2 && <Divider>{address.addressLine2}</Divider>}
            {address.addressLine3 && <Divider>{address.addressLine3}</Divider>}
            {address.addressLine4 && <Divider>{address.addressLine4}</Divider>}
            {address.city && <Divider>{address.city}</Divider>}
            {address.country && <Divider>{address.country}</Divider>}
            {address.postcode && <Divider>{address.postcode}</Divider>}
        </div>
    );

    const addressSections = (addresses = []) =>
        addresses.length > 0 ? (
            getKeyTypes(addresses).map(key =>
                addressSection(
                    key,
                    addresses.filter(add => add.addressType === key)
                )
            )
        ) : (
            <NotProvidedSpan>
                Please contact your adviser to provide your address information
            </NotProvidedSpan>
        );

    const getKeyTypes = (addresses = []) =>
        uniqBy(addresses, "addressType")
            .map(add => add.addressType)
            .sort();

    const getTitle = (addresses = []) => (addresses.length > 1 ? "Addresses" : "Address");

    const refetch = () => {
        retryFetch(requestStatus.href, setAddresses, "addresses");
    };

    const { message, action } = getRefetchMessageAndAction(requestStatus, refetch);

    return (
        <div>
            <H2 secondary textAlign="center">
                {getTitle(addresses)}
            </H2>
            <AddressesSubSection>
                <Message
                    severity={ERROR}
                    text={message}
                    errorCode={requestStatus.errorCode}
                    shown={!!message}
                    padding="0px"
                    action={action}
                />
                {isSuccess(requestStatus.status) && addresses && addressSections(addresses)}
                <ClearingFloat />
            </AddressesSubSection>
        </div>
    );
}

export const AddressesSubSection = styled.div`
    background: ${props => props.theme.tertiary};
    padding: 1.5em;
`;

export const NotProvidedSpan = styled.div`
    font-style: italic;
    color: red;
`;

Addresses.defaultProps = {
    addresses: [],
    requestStatus: {
        status: IN_PROGRESS
    }
};

Addresses.propTypes = {
    addresses: PropTypes.arrayOf(
        PropTypes.shape({
            addressId: PropTypes.number,
            addressType: PropTypes.string,
            primary: PropTypes.bool,
            addressLine1: PropTypes.string,
            addressLine2: PropTypes.string,
            addressLine3: PropTypes.string,
            addressLine4: PropTypes.string,
            city: PropTypes.string,
            country: PropTypes.string,
            postcode: PropTypes.string
        })
    ),
    requestStatus: requestObject
};
